'use client'

import { ColorCode, Image, Spacer } from '@vinted/web-ui'
import { Fragment, RefObject, useEffect, useState } from 'react'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import { colorCodeToHex } from '@marketplace-web/vendor-abstractions/web-ui-util'
import { findThumbnail } from '@marketplace-web/shared/utils'

enum ItemThumbnailSize {
  Medium = 'thumb150x210',
}

const getItemPhotoThumbnail = (
  photos:
    | Array<{
        isMain: boolean
        thumbnails: Array<{ type: string; url: string }>
      }>
    | undefined,
  size = ItemThumbnailSize.Medium,
) => {
  const photo = photos?.find(({ isMain }) => isMain)

  return findThumbnail(photo, size)
}

type Props = {
  items: Array<ProductItemModel>
  /**
   * If true, the number of visible thumbnails will be calculated dynamically
   * based on the container width that is passed through `containerRef` prop.
   */
  isDynamic?: boolean
  containerRef?: RefObject<HTMLDivElement | null>
  visibleThumbnailsLimit?: number
}

const VISIBLE_THUMBNAILS_LIMIT = 3
const THUMBNAIL_WIDTH_WITH_MARGIN = 40

const ItemThumbnails = ({
  items,
  containerRef,
  isDynamic = false,
  visibleThumbnailsLimit = VISIBLE_THUMBNAILS_LIMIT,
}: Props) => {
  const [dynamicVisibleThumbnailsLimit, setDynamicVisibleThumbnailsLimit] = useState(0)

  const displayLimit = isDynamic ? dynamicVisibleThumbnailsLimit : visibleThumbnailsLimit
  const showHiddenThumbnailsCount = items.length > displayLimit

  useEffect(() => {
    if (!isDynamic || !containerRef) return undefined

    const updateThumbnailCount = () => {
      const containerOffsetWidth = containerRef.current?.offsetWidth

      if (!containerOffsetWidth) return

      const hiddenThumbnailLabelCount = showHiddenThumbnailsCount ? 1 : 0

      setDynamicVisibleThumbnailsLimit(
        Math.floor(containerOffsetWidth / THUMBNAIL_WIDTH_WITH_MARGIN) - hiddenThumbnailLabelCount,
      )
    }

    updateThumbnailCount()

    window.addEventListener('resize', updateThumbnailCount)

    return () => {
      window.removeEventListener('resize', updateThumbnailCount)
    }
  }, [items.length, isDynamic, showHiddenThumbnailsCount, containerRef])

  return (
    <SeparatedList separator={<Spacer orientation={Spacer.Orientation.Vertical} />}>
      {items.slice(0, displayLimit).map(item => (
        <Fragment key={item.id}>
          <Image
            src={getItemPhotoThumbnail(item.photos)}
            styling={Image.Styling.Rounded}
            size={Image.Size.Medium}
            scaling={Image.Scaling.Cover}
          />
        </Fragment>
      ))}
      {showHiddenThumbnailsCount && (
        <Image
          label={`+${items.length - displayLimit}`}
          styling={Image.Styling.Rounded}
          size={Image.Size.Medium}
          scaling={Image.Scaling.Cover}
          color={colorCodeToHex(ColorCode.GreyscaleLevel4)}
        />
      )}
    </SeparatedList>
  )
}

export default ItemThumbnails
